import {
    getProgramPackClassFromMetadata,
    getProgramType,
} from '@rhg/utils/programs';
import { sortTools } from '@rhg/utils/tools';
import { getWorkflowPackClass } from '@rhg/utils/workflows';
import { useMemo } from 'react';
import {
    ProgramContextProps,
    ProgramContextState,
    ToolGuideMap,
} from './types';

export function useProgramContext({
    program: _program,
    programMetadata,
    packClass: providedPackClass,
    packProgram,
    workflow,
    recommendation,
    contentFeedbackId,
    isFetched = true,
}: ProgramContextProps): ProgramContextState {
    const program = programMetadata ?? _program ?? packProgram;
    // Prioritize filtered count over totalGuides since backend includes trailer in totalGuides
    const numSessions = useMemo(
        () =>
            _program?.guides?.filter((guide) => !guide.isTrailer).length ??
            programMetadata?.totalGuides ??
            0,
        [programMetadata, _program?.guides]
    );

    const programType = useMemo(
        () => getProgramType(program, workflow),
        [program, workflow]
    );

    const toolsByGuide = useMemo(() => {
        if (!(program && 'tools' in program)) {
            return {};
        }
        return (
            // TODO [EVE-7805]: what types actually have tools?
            program.tools
                ?.filter((tool) => !!tool?.assets)
                ?.sort(sortTools)
                .reduce<ToolGuideMap>(
                    (toolGuideMap, tool) => ({
                        ...toolGuideMap,
                        [tool.guideId]: toolGuideMap[tool.guideId]
                            ? [...toolGuideMap[tool.guideId], tool]
                            : [tool],
                    }),
                    {}
                ) ?? {}
        );
    }, [program]);

    const packClass = useMemo(() => {
        if (providedPackClass) {
            return providedPackClass;
        }
        const programPackClass = getProgramPackClassFromMetadata(program);
        if (programPackClass) {
            return programPackClass;
        }
        if (workflow) {
            return getWorkflowPackClass(workflow);
        }
        return undefined;
    }, [program, providedPackClass, workflow]);

    return {
        program: _program,
        programMetadata,
        packClass,
        packProgram,
        programType,
        numSessions,
        toolsByGuide,
        recommendation,
        contentFeedbackId,
        isFetched,
        _workflow: workflow,
    };
}
